export function blobToFile(blob: Blob, fileName: string): File {
  return new File([blob], fileName, {
    lastModified: new Date().getTime(),
    type: blob.type,
  });
}

/**
 * Extrae el mensaje de error de un cuerpo de respuesta.
 *
 * Cuando la petición usa `responseType: 'blob'`, el cuerpo de un error llega
 * como Blob y no como JSON, así que `error.data.message` viene undefined. Hay
 * que leer el Blob y contemplar las dos formas posibles del cuerpo:
 *  - texto plano: el filtro global de la API responde `exception.getResponse()`,
 *    que para `new HttpException('mensaje', 409)` es el string pelado.
 *  - JSON `{ message }`: el resto de las excepciones de Nest.
 *
 * Devuelve `null` si no hay un mensaje utilizable (cuerpo vacío, ilegible o de
 * una forma inesperada), para que quien llame use su mensaje genérico.
 */
export async function readErrorMessageFromBlob(
  data: unknown,
): Promise<string | null> {
  try {
    const raw = data instanceof Blob ? await data.text() : data;
    if (typeof raw !== 'string') {
      return extractMessage(raw);
    }

    const trimmed = raw.trim();
    if (!trimmed) {
      return null;
    }

    try {
      return extractMessage(JSON.parse(trimmed));
    } catch {
      // No es JSON: el cuerpo es el mensaje mismo.
      return trimmed;
    }
  } catch {
    return null;
  }
}

function extractMessage(body: any): string | null {
  if (typeof body === 'string') {
    return body.trim() || null;
  }
  const message = body?.message;
  if (typeof message === 'string') {
    return message.trim() || null;
  }
  if (Array.isArray(message)) {
    return message.filter((item) => typeof item === 'string').join(' ') || null;
  }
  return null;
}

export function downloadFromBlob(blob: Blob, qualifiedName: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', qualifiedName);
  link.click();
}
